import { useEffect, useState } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import type { FieldSelectedSections } from '@mui/x-date-pickers/models';
import type { Dayjs } from 'dayjs';

interface FilterDatePickerProps {
  label: string;
  resetKey: number;
  defaultValue?: Dayjs | null;
  onCommit: (value: Dayjs | null) => void;
}

const SEPARATORS = new Set(['.', ',', '/', '-']);

function filledSection(value: string, start: number): 'day' | 'month' | 'year' | null {
  const from = start < 3 ? 0 : start < 6 ? 3 : 6;
  const to = start < 3 ? 2 : start < 6 ? 5 : 10;
  return /^\d+$/.test(value.slice(from, to)) ? (start < 3 ? 'day' : start < 6 ? 'month' : 'year') : null;
}

/** Date filter that keeps the caret while a date is typed. The query runs only for a full year. */
export default function FilterDatePicker({
  label,
  resetKey,
  defaultValue,
  onCommit,
}: FilterDatePickerProps) {
  const [selectedSections, setSelectedSections] = useState<FieldSelectedSections>(null);

  useEffect(() => {
    setSelectedSections(null);
  }, [resetKey]);

  return (
    <DatePicker
      key={resetKey}
      label={label}
      format="DD.MM.YYYY"
      defaultValue={defaultValue ?? undefined}
      selectedSections={selectedSections}
      onSelectedSectionsChange={setSelectedSections}
      onChange={(value: Dayjs | null) => {
        if (value != null && value.isValid() && value.year() >= 1900) onCommit(value);
        else onCommit(null);
      }}
      slotProps={{
        textField: {
          size: 'small',
          fullWidth: true,
          onKeyDown: (event) => {
            if (!SEPARATORS.has(event.key)) return;
            event.preventDefault();
            const input = event.target as HTMLInputElement;
            const start = input.selectionStart ?? 0;
            const end = input.selectionEnd ?? start;
            if (start === 0 && end - start > 2) return;
            const section = filledSection(input.value, start);
            if (section === 'day') setSelectedSections('month');
            else if (section === 'month') setSelectedSections('year');
          },
        },
      }}
    />
  );
}
